/**
 * @fileoverview Companies info table component
 *
 * This component renders the main companies table with resizable columns.
 * It handles column width management, sticky first column, and provides
 * context for child components.
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts} - Type definitions
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/HeadingInfoTable.tsx} - Table header component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/CompaniesInfoTableRow.tsx} - Table row component
 */

"use client";

import { memo, useEffect, useRef, useState } from "react";

import { Table, TableContainer, Tbody, Thead } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import { useActions } from "@/hooks/useActions";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import { thinkScrollbar } from "@/utils/thinkScrollbar";

import type { Company } from "../../types";
import TableContext from "../context/TableContext";

import CompaniesInfoTableRow from "./CompaniesInfoTableRow";
import HeadingInfoTable from "./HeadingInfoTable";

/**
 * Get minimum width for a column based on its content type
 *
 * @param columnId - ID of the column
 * @returns Minimum width in pixels
 */
const getMinColumnWidth = (columnId: string): number => {
  switch (columnId) {
    case "links":
      return 200; // Minimum width for Links column to fit social icons
    case "company":
      return 200; // Minimum width for company name and logo
    case "save":
      return 100; // Minimum width for Save button to prevent wrapping
    default:
      return 60; // Default minimum width
  }
};

/**
 * Companies info table component
 *
 * @param props - Component props
 */
const CompaniesInfoTable = ({ companies }: { companies: Company[] }) => {
  // Debug table mounting and data
  console.log("CompaniesInfoTable - Mounting:", {
    companiesCount: companies?.length,
    firstCompany: companies?.[0],
  });

  const { setViewParams, setSavedViewParams } = useActions();
  const { currentParams, currentView } = useGetCurrentView();
  const tableRef = useRef<HTMLTableElement>(null);
  const [columnsWidth, setColumnsWidth] = useState<number[]>(
    currentParams?.columnsWidth || []
  );
  const [selectedRows, setSelectedRows] = useState<Company[]>([]);
  const [initialColumnsWidth, setInitialColumnsWidth] = useState<number[]>(
    currentParams?.columnsWidth || []
  );

  // Debug table styles
  useEffect(() => {
    const table = tableRef.current;
    if (table) {
      console.log("CompaniesInfoTable - Styles:", {
        tableElement: table,
        computedStyles: window.getComputedStyle(table),
        borderCollapse: window.getComputedStyle(table).borderCollapse,
        borderSpacing: window.getComputedStyle(table).borderSpacing,
        rows: Array.from(table.querySelectorAll("tr")).map((tr) => ({
          id: tr.getAttribute("data-row-id"),
          borderBottom: window.getComputedStyle(tr).borderBottom,
          backgroundColor: window.getComputedStyle(tr).backgroundColor,
        })),
      });
    }
  }, [companies]);

  useEffect(() => {
    const allHeads: HTMLElement[] = [];
    if (typeof window !== "undefined") {
      document.querySelectorAll(".head").forEach((item) => {
        allHeads.push(item as HTMLElement);
      });

      // Get width of each header element, ensuring minimum widths
      const elWidth = allHeads.map((item) => {
        const columnId = item.getAttribute("id") || "";
        const measuredWidth = item.clientWidth;
        const minWidth = getMinColumnWidth(columnId);
        return Math.max(measuredWidth, minWidth);
      });

      // Set column widths from params or measured widths
      setColumnsWidth(currentParams?.columnsWidth || elWidth);
      setInitialColumnsWidth(currentParams?.columnsWidth || elWidth);

      // Save column widths if not already set
      if (!currentParams?.columnsWidth?.length) {
        if (currentView?.id) {
          const widthParams = { columnsWidth: elWidth };
          setViewParams({
            key: currentView.id,
            params: widthParams,
          });
          setSavedViewParams({
            key: currentView.id,
            params: widthParams,
          });
        }
      }
    }
  }, [
    currentParams?.columnsWidth,
    currentView?.id,
    setViewParams,
    setSavedViewParams,
  ]);

  return (
    <TableContext.Provider
      value={{
        selectedRows,
        setSelectedRows,
        columnsWidth,
        initialColumnsWidth,
        setInitialColumnsWidth,
      }}
    >
      <TableContainer
        ref={tableRef}
        maxH={"calc(100vh - 300px)"}
        overflowY={"auto"}
        overflowX={"auto"}
        sx={{
          ...thinkScrollbar,
          // Remove any inherited table styles that might cause issues
          table: {
            borderCollapse: "separate !important",
            borderSpacing: "0 !important",
            isolation: "isolate !important", // Create a new stacking context
            position: "relative !important",
            "& tr": {
              borderBottom: "none !important",
              borderColor: "transparent !important",
              backgroundColor: "transparent !important",
            },
            "& td": {
              borderBottom: "none !important",
              borderColor: "transparent !important",
              backgroundColor: "transparent !important",
            },
            // Enhanced styling for sticky columns
            '& [data-company-column="true"]': {
              position: "sticky !important", // Ensure sticky positioning is enforced
              left: "0 !important", // Ensure the column sticks to the left
              zIndex: "20 !important", // Keep high z-index
              isolation: "isolate !important",
              boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15) !important", // Add shadow for depth
              backgroundColor: "white !important", // Use explicit white instead of inherit
              // Use border instead of pseudo-element
              borderRight: "4px solid white !important",
            },
          },
        }}
      >
        <Table
          variant={"unstyled"}
          className="sticky-first-column-table"
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <Thead>
            <HeadingInfoTable
              companies={companies}
              tableRef={tableRef}
              columnsWidth={columnsWidth}
              setColumnsWidth={setColumnsWidth}
              initialColumnsWidth={initialColumnsWidth}
            />
          </Thead>
          <Tbody>
            {companies?.map((item, idx) => (
              <CompaniesInfoTableRow key={item?.id} item={item} idx={idx} />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </TableContext.Provider>
  );
};

CompaniesInfoTable.displayName = "CompaniesInfoTable";

export default memo(CompaniesInfoTable);
